import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsApi } from '../utils/analytics';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Analytics Provider Component
 * Automatically tracks:
 * - Session initialization on first load
 * - Page views on route changes
 * - Session end on page unload
 */
const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const location = useLocation();
  const initialized = useRef(false);
  const initPromise = useRef<Promise<void> | null>(null);
  const lastPath = useRef<string>('');

  // Initialize session on mount
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      // Store the initialization promise so we can wait for it
      initPromise.current = analyticsApi.initSession().then(() => {
        console.debug('[Analytics] Session initialized');
      }).catch((error) => {
        console.debug('[Analytics] Session initialization failed:', error);
      });

      // Track session end on page unload
      const handleUnload = () => {
        analyticsApi.endSession();
      };

      window.addEventListener('beforeunload', handleUnload);

      // Also track when visibility changes (tab switch, minimize)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          analyticsApi.endSession();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.removeEventListener('beforeunload', handleUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const currentPath = location.pathname;

    // Don't track the same path twice in a row
    if (currentPath !== lastPath.current) {
      lastPath.current = currentPath;

      // Wait for session initialization before tracking page view
      if (initPromise.current) {
        initPromise.current.then(() => {
          analyticsApi.trackPageView(currentPath);
        });
      } else {
        // If init already completed, track immediately
        analyticsApi.trackPageView(currentPath);
      }
    }
  }, [location.pathname]);

  return <>{children}</>;
};

export default AnalyticsProvider;

// Export a hook for manual event tracking
export const useAnalytics = () => {
  return {
    trackEvent: analyticsApi.trackEvent,
    linkUser: analyticsApi.linkUser,
  };
};

